import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import api from '../../utils/api';
import { CheckCircle, Hourglass, User, Landmark, Lock, Lightbulb, Save, Loader2 } from 'lucide-react';

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [saving, setSaving] = useState(false);

  const [profileForm, setProfileForm] = useState({ name: user?.name || '', mobile: user?.mobile || '' });
  const [bankForm, setBankForm] = useState({
    accountName: user?.bankDetails?.accountName || '',
    accountNumber: user?.bankDetails?.accountNumber || '',
    ifscCode: user?.bankDetails?.ifscCode || '',
    bankName: user?.bankDetails?.bankName || '',
    upiId: user?.bankDetails?.upiId || '',
    usdtAddress: user?.bankDetails?.usdtAddress || '',
  });
  const [passForm, setPassForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put('/users/profile', profileForm);
      await refreshUser();
      toast.success('Profile updated successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update');
    } finally { setSaving(false); }
  };

  const handleSaveBank = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put('/users/bank', bankForm);
      await refreshUser();
      toast.success('Bank details updated successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update');
    } finally { setSaving(false); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passForm.newPassword !== passForm.confirmPassword) return toast.error('Passwords do not match');
    if (passForm.newPassword.length < 6) return toast.error('Password must be at least 6 characters');
    setSaving(true);
    try {
      await api.put('/users/password', { currentPassword: passForm.currentPassword, newPassword: passForm.newPassword });
      toast.success('Password changed successfully');
      setPassForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to change password');
    } finally { setSaving(false); }
  };

  return (
    <DashboardLayout>
      <h1 className="page-title">My Profile</h1>
      <p className="page-subtitle">Manage your account settings</p>

      {/* User card */}
      <div className="card" style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
        <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'linear-gradient(135deg, var(--primary), var(--primary-dark))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.5rem', fontWeight: 700, color: '#fff', flexShrink: 0 }}>
          {user?.name?.[0]?.toUpperCase()}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: 'Rajdhani', fontSize: '1.6rem', fontWeight: 700, color: 'var(--text-white)' }}>{user?.name}</div>
          <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>{user?.email} &nbsp;|&nbsp; {user?.mobile}</div>
          <div style={{ display: 'flex', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
            <span className="badge badge-gold">Code: {user?.referralCode}</span>
            <span className={`badge ${user?.isActive ? 'badge-success' : 'badge-warning'}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>{user?.isActive ? <><CheckCircle size={12} /> Active</> : <><Hourglass size={12} /> Inactive</>}</span>
            {user?.sponsor && <span className="badge badge-info">Sponsor: {user?.sponsor?.referralCode || user?.sponsorCode}</span>}
          </div>
        </div>
      </div>

      <div className="tab-bar">
        {[['profile', User, 'Profile'], ['bank', Landmark, 'Bank Details'], ['password', Lock, 'Password']].map(([id, Icon, label]) => (
          <button key={id} className={`tab-btn ${activeTab === id ? 'active' : ''}`} onClick={() => setActiveTab(id)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {activeTab === 'profile' && (
        <div className="card">
          <div className="card-header"><h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><User size={18} /> Personal Information</h3></div>
          <form onSubmit={handleSaveProfile}>
            <div className="grid-2" style={{ gap: '0 20px' }}>
              <div className="form-group">
                <label>Full Name</label>
                <input type="text" className="form-control" value={profileForm.name} onChange={e => setProfileForm(f => ({ ...f, name: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label>Mobile Number</label>
                <input type="tel" className="form-control" value={profileForm.mobile} onChange={e => setProfileForm(f => ({ ...f, mobile: e.target.value }))} required maxLength={10} />
              </div>
            </div>
            <div className="form-group">
              <label>Email Address</label>
              <input type="email" className="form-control" value={user?.email} disabled style={{ opacity: 0.6, cursor: 'not-allowed' }} />
              <small style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>Email cannot be changed</small>
            </div>
            <div className="form-group">
              <label>Referral Code</label>
              <input type="text" className="form-control" value={user?.referralCode} disabled style={{ opacity: 0.6, cursor: 'not-allowed', letterSpacing: '2px', color: 'var(--primary)' }} />
            </div>
            <button type="submit" className="btn btn-primary" disabled={saving} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {saving ? <><Loader2 size={16} className="spin" /> Saving...</> : <><Save size={16} /> Save Profile</>}
            </button>
          </form>
        </div>
      )}

      {activeTab === 'bank' && (
        <div className="card">
          <div className="card-header"><h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Landmark size={18} /> Bank / UPI Details</h3></div>
          <div className="alert alert-info" style={{ marginBottom: 20, fontSize: '0.85rem', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <Lightbulb size={16} style={{ flexShrink: 0, marginTop: 2 }} />
            <span>Bank details are required for processing withdrawals. Make sure the details match your bank account.</span>
          </div>
          <form onSubmit={handleSaveBank}>
            <div className="grid-2" style={{ gap: '0 20px' }}>
              <div className="form-group">
                <label>Account Holder Name</label>
                <input type="text" className="form-control" placeholder="Full name as on bank account" value={bankForm.accountName} onChange={e => setBankForm(f => ({ ...f, accountName: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Bank Name</label>
                <input type="text" className="form-control" placeholder="e.g. SBI, HDFC, ICICI" value={bankForm.bankName} onChange={e => setBankForm(f => ({ ...f, bankName: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Account Number</label>
                <input type="text" className="form-control" placeholder="Bank account number" value={bankForm.accountNumber} onChange={e => setBankForm(f => ({ ...f, accountNumber: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>IFSC Code</label>
                <input type="text" className="form-control" placeholder="e.g. SBIN0001234" value={bankForm.ifscCode} onChange={e => setBankForm(f => ({ ...f, ifscCode: e.target.value.toUpperCase() }))} />
              </div>
            </div>
            <div className="form-group">
              <label>UPI ID <span style={{ color: 'var(--text-muted)' }}>(Optional)</span></label>
              <input type="text" className="form-control" placeholder="e.g. yourname@upi" value={bankForm.upiId} onChange={e => setBankForm(f => ({ ...f, upiId: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>USDT Wallet Address <span style={{ color: 'var(--text-muted)' }}>(Optional — needed only for USDT withdrawals)</span></label>
              <input type="text" className="form-control" placeholder="e.g. TRC20/BEP20 wallet address" value={bankForm.usdtAddress} onChange={e => setBankForm(f => ({ ...f, usdtAddress: e.target.value }))} />
            </div>
            <button type="submit" className="btn btn-primary" disabled={saving} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {saving ? <><Loader2 size={16} className="spin" /> Saving...</> : <><Save size={16} /> Save Bank Details</>}
            </button>
          </form>
        </div>
      )}

      {activeTab === 'password' && (
        <div className="card" style={{ maxWidth: 480 }}>
          <div className="card-header"><h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Lock size={18} /> Change Password</h3></div>
          <form onSubmit={handleChangePassword}>
            {[
              { label: 'Current Password', key: 'currentPassword', placeholder: 'Enter current password' },
              { label: 'New Password', key: 'newPassword', placeholder: 'Min 6 characters' },
              { label: 'Confirm New Password', key: 'confirmPassword', placeholder: 'Repeat new password' },
            ].map(f => (
              <div key={f.key} className="form-group">
                <label>{f.label}</label>
                <input type="password" className="form-control" placeholder={f.placeholder} value={passForm[f.key]} onChange={e => setPassForm(p => ({ ...p, [f.key]: e.target.value }))} required />
              </div>
            ))}
            <button type="submit" className="btn btn-primary" disabled={saving} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {saving ? <><Loader2 size={16} className="spin" /> Changing...</> : <><Lock size={16} /> Change Password</>}
            </button>
          </form>
        </div>
      )}
    </DashboardLayout>
  );
}